// third-party libraries
import type { DashboardDoctor, RootFiltersInput, InsightsMetric, DashboardAccount } from "@happy-doc/shared-types";
import { DateRangeOptions, } from "@happy-doc/shared-types";
import * as Sentry from "@sentry/react";

// react and hooks
import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { getChildAccounts, getSharedMetricsWithCategories } from "../lib/supabase";
import { useAuth } from "./AuthContext";
import { type TabType } from "@happy-doc/shared-components";
import { mapChildAccountsToDashboardAccount } from "../utils/helpers";
import { useTabHierarchy, useMetricAccess } from "@happy-doc/insights-shared";
import moment from "moment";

// MAGIC STRINGS
const LOCAL_STORAGE_SELECTED_DOCTOR_IDS_KEY = "enterprise:insights:selectedDoctorIds";
const LOCAL_STORAGE_SELECTED_ACCOUNT_IDS_KEY = "enterprise:insights:selectedAccountIds";
const LOCAL_STORAGE_SELECTED_DATE_RANGE_BUTTON_VALUE_KEY = "enterprise:insights:selectedDateRangeButtonValue";
const LOCAL_STORAGE_SELECTED_WEEK_KEY = "enterprise:insights:selectedWeek";

/*
* An interface for the context value of the DashboardStateContext
* This handles everything related to the state of the dashboard's content and selections 
* (but not metric data)
*/
interface DashboardStateContextValue {
    dashboardDoctors: DashboardDoctor[];
    setDashboardDoctors: (doctors: DashboardDoctor[]) => void;
    selectedDoctorIds: string[];
    setSelectedDoctorIds: (doctorIds: string[]) => void;
    dashboardAccounts: DashboardAccount[];
    setDashboardAccounts: (accounts: DashboardAccount[]) => void;
    selectedAccountIds: string[];
    setSelectedAccountIds: (accountIds: string[]) => void;
    selectedDateRangeButtonValue: DateRangeOptions;
    setSelectedDateRangeButtonValue: (value: DateRangeOptions) => void;
    handleTabClick: (primarySlug: string, secondarySlug: string | null) => void;
    hasPremiumAccess: boolean;
    setHasPremiumAccess: (hasPremiumAccess: boolean) => void;
    rootFilters: RootFiltersInput | null;
    setRootFilters: (filters: RootFiltersInput | null) => void;
    activeTabSlugs: { primary: string | null; secondary: string | null };
    visibleMetrics: InsightsMetric[];
    primaryTabs: TabType[];
    secondaryTabs: TabType[];
    hasPremiumMetrics: boolean;
    selectedWeek: { startDate: string, endDate: string };
    setSelectedWeek: React.Dispatch<React.SetStateAction<{
        startDate: string;
        endDate: string;
    }>>
}

/*
* An interface for the context value of the DashboardMetricsContext
* This handles everything related to the metrics data and keys
*/
interface DashboardMetricsContextValue {
    metrics: InsightsMetric[];
}

/*
* An interface for the value of the DashboardContext
*/
interface DashboardContextValue {
    dashboardStateContext: DashboardStateContextValue;
    dashboardMetricsContext: DashboardMetricsContextValue;
}

// create the context
const DashboardContext = createContext<DashboardContextValue | undefined>(undefined);

// create a hook to use the context
export function useDashboardContext(): DashboardContextValue {
    const context = useContext(DashboardContext);
    if (!context) {
        throw new Error("useDashboardContext must be used within a DashboardProvider");
    }
    return context;
}


/*
* getLocalSelectedDoctorIDs is a helper function that gets the selectedDoctorIds from localStorage
* @returns {string[]} - an array of strings that represent the selected doctor ids
*/
const getLocalSelectedDoctorIDs = (): string[] => {
    const localStorageSelectedDoctorIds = localStorage.getItem(LOCAL_STORAGE_SELECTED_DOCTOR_IDS_KEY);

    let ids: string[] = [];
    try {
        ids = JSON.parse(localStorageSelectedDoctorIds || "[]");
    } catch (e: any) {
        Sentry.captureException("Error getting selectedDoctorIds from localStorage", e);
    }

    return ids;
}

const getLocalSelectedAccountIDs = (): string[] => {
    const localStorageSelectedAccountIds = localStorage.getItem(LOCAL_STORAGE_SELECTED_ACCOUNT_IDS_KEY);

    let ids: string[] = [];
    try {
        ids = JSON.parse(localStorageSelectedAccountIds || "[]");
    } catch (e: any) {
        Sentry.captureException("Error getting selectedAccountIds from localStorage", e);
    }

    return ids;
}

/*
* getLocalSelectedDateRangeButtonValue is a helper function that gets the selectedDateRangeButtonValue from localStorage
* @returns {DateRangeOptions} - a DateRangeOptions value that represents the selected date range button value
*/
const getLocalSelectedDateRangeButtonValue = (): DateRangeOptions => {
    const localStorageSelectedDateRangeButtonValue = localStorage.getItem(LOCAL_STORAGE_SELECTED_DATE_RANGE_BUTTON_VALUE_KEY);

    let value: DateRangeOptions = DateRangeOptions.Days7;
    try {
        if (localStorageSelectedDateRangeButtonValue) {
            value = JSON.parse(localStorageSelectedDateRangeButtonValue);
        }
    } catch (e: any) {
        Sentry.captureException("Error getting selectedDateRangeButtonValue from localStorage", e);
    }

    return value;
}

/*
* getLocalSelectedWeek is a helper function that gets the selectedWeek from localStorage
* @returns {{ startDate: string, endDate: string }} - an object with startDate and endDate that represents the selected week
*/
const getLocalSelectedWeek = (): { startDate: string, endDate: string } => {
    const localStorageSelectedWeek = localStorage.getItem(LOCAL_STORAGE_SELECTED_WEEK_KEY);

    // Default to previous week
    let week: { startDate: string, endDate: string } = {
        startDate: moment().subtract(1, 'week').startOf('week').toISOString(),
        endDate: moment().subtract(1, 'week').endOf('week').toISOString()
    };

    try {
        if (localStorageSelectedWeek) {
            const parsed = JSON.parse(localStorageSelectedWeek);
            if (
                parsed &&
                typeof parsed === 'object' &&
                typeof parsed.startDate === 'string' &&
                typeof parsed.endDate === 'string'
            ) {
                week = {
                    startDate: parsed.startDate,
                    endDate: parsed.endDate
                };
            }
        }
    } catch (e: any) {
        Sentry.captureException("Error getting selectedWeek from localStorage", e);
    }

    return week;
};

/*
* The DashboardProvider is a context provider that wraps the entire dashboard page.
* It provides the context for the dashboard's state and metrics data.
*/
export function DashboardProvider({ children }: { children: ReactNode }) {
    // get the session - note that this may have to become injected if this dashboard is hosted outside of the main app
    const { session, account, isLoading } = useAuth();

    // DASHBOARD STATE
    const [dashboardDoctors, setDashboardDoctors] = useState<DashboardDoctor[]>([]);
    const [dashboardAccounts, setDashboardAccounts] = useState<DashboardAccount[]>([]);
    const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
    const [selectedDateRangeButtonValue, setSelectedDateRangeButtonValue] = useState<DateRangeOptions>(getLocalSelectedDateRangeButtonValue());
    const [selectedDoctorIds, setSelectedDoctorIds] = useState<string[]>(getLocalSelectedDoctorIDs());
    const [selectedWeek, setSelectedWeek] = useState<{ startDate: string, endDate: string }>(getLocalSelectedWeek());

    const [rootFilters, setRootFilters] = useState<RootFiltersInput | null>(null);
    const [metrics, setMetrics] = useState<InsightsMetric[]>([]);

    // Fetch accounts and handle selection
    useEffect(() => {
        const abortController = new AbortController();
        const signal = abortController.signal;

        const fetchChildAccounts = async (accountId: string) => {
            const accounts = await getChildAccounts(accountId, signal);
            const dashboardAccounts = accounts.map((acc) => mapChildAccountsToDashboardAccount(acc)).filter((acc) => acc !== undefined) as DashboardAccount[];

            // Set the accounts first
            setDashboardAccounts(dashboardAccounts);

            // Then handle account selection
            if (dashboardAccounts.length > 0) {
                const storedIds = getLocalSelectedAccountIDs();

                // Filter out any selected account IDs that don't exist in the loaded accounts
                const validSelectedIds = storedIds.filter(id =>
                    dashboardAccounts.some(account => account.id === id)
                );

                // If no valid selections remain, select the first non-archived account
                if (validSelectedIds.length === 0) {
                    const firstNonArchivedAccount = dashboardAccounts.find(acc => !acc.isArchived);
                    if (firstNonArchivedAccount) {
                        setSelectedAccountIds([firstNonArchivedAccount.id]);
                    } else {
                        setSelectedAccountIds([dashboardAccounts[0].id]);
                    }
                } else {
                    setSelectedAccountIds(validSelectedIds);
                }
            }
        }

        if (account?.id && session?.user?.id && !isLoading) {
            fetchChildAccounts(account?.id);
        }

        return () => {
            abortController.abort();
        };
    }, [account?.id, session?.user?.id, isLoading]);


    useEffect(() => {
        const fetchComponents = async () => {
            const abortController = new AbortController();
            const abortSignal = abortController.signal;
            const sharedMetrics = await getSharedMetricsWithCategories(abortSignal);
            setMetrics(sharedMetrics);
        };
        if (account?.id && session?.user?.id && !isLoading) {
            fetchComponents();
        }
    }, [session, account?.id, isLoading]);

    // save selectedDoctorIds to localStorage when it changes
    useEffect(() => {
        localStorage.setItem(LOCAL_STORAGE_SELECTED_DOCTOR_IDS_KEY, JSON.stringify(selectedDoctorIds));
    }, [selectedDoctorIds]);

    // save selectedAccountIds to localStorage when it changes
    useEffect(() => {
        localStorage.setItem(LOCAL_STORAGE_SELECTED_ACCOUNT_IDS_KEY, JSON.stringify(selectedAccountIds));
    }, [selectedAccountIds]);

    // save selectedDateRangeButtonValue to localStorage when it changes
    useEffect(() => {
        localStorage.setItem(LOCAL_STORAGE_SELECTED_DATE_RANGE_BUTTON_VALUE_KEY, JSON.stringify(selectedDateRangeButtonValue));
    }, [selectedDateRangeButtonValue]);

    // save selectedWeek to localStorage when it changes
    useEffect(() => {
        localStorage.setItem(LOCAL_STORAGE_SELECTED_WEEK_KEY, JSON.stringify(selectedWeek));
    }, [selectedWeek]);

    const { accessibleMetrics, hasPremiumMetrics } = useMetricAccess({
        metrics,
        hasPremiumAccess: true
    });

    const {
        primaryTabs,
        secondaryTabs,
        visibleMetrics,
        activeTabSlugs,
        handleTabClick
    } = useTabHierarchy({
        metrics: accessibleMetrics,
        hasPremiumAccess: true
    });

    // CONTEXT VALUES
    // set the dashboard state context value
    const dashboardStateContextValue: DashboardStateContextValue = {
        dashboardDoctors,
        setDashboardDoctors,
        selectedDoctorIds,
        setSelectedDoctorIds,
        dashboardAccounts,
        setDashboardAccounts,
        selectedAccountIds,
        setSelectedAccountIds,
        selectedDateRangeButtonValue,
        setSelectedDateRangeButtonValue,
        handleTabClick,
        hasPremiumAccess: true,
        setHasPremiumAccess: () => { },
        rootFilters,
        setRootFilters,
        activeTabSlugs,
        visibleMetrics,
        primaryTabs,
        secondaryTabs,
        hasPremiumMetrics: hasPremiumMetrics,
        selectedWeek,
        setSelectedWeek
    }

    // set the metric keys and data in the provider
    const dashboardMetricsContextValue: DashboardMetricsContextValue = {
        metrics: accessibleMetrics,
    }

    // return the provider with the context values
    return (
        <DashboardContext.Provider value={{
            dashboardStateContext: dashboardStateContextValue,
            dashboardMetricsContext: dashboardMetricsContextValue,
        }}>
            {children}
        </DashboardContext.Provider>
    );
}
